import { useState } from "react";
import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import {
  useAdminEvents,
  invalidateAdminEvents,
  useAdminVehicles,
  useAlert,
  useApi,
} from "../../hooks";
import { useLoaderStore } from "../../context";
import { API } from "../../utils/constants";
import { ActiveToggle, UseMyLocationButton } from "../../components/Inputs";

/**
 * formatEventDate — Formats an ISO date (YYYY-MM-DD) as "Sat, Jul 4 2026".
 * @param {string} date
 * @returns {string}
 */
function formatEventDate(date) {
  if (!date) return "";
  return moment(date, "YYYY-MM-DD").format("ddd, MMM D YYYY");
}

// ── Event create/edit modal ─────────────────────────────────────────────────

/**
 * EventModal — Create or edit an event (CON-002 §9).
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {object|null} props.event - If set, edit mode.
 * @param {Array} props.vehicles - Vehicles for the optional hosting-rig select.
 */
function EventModal({ open, onClose, event = null, vehicles = [] }) {
  const isEdit = !!event;
  const alert = useAlert();
  const { post, put } = useApi();
  const { setShowLoader } = useLoaderStore();

  const [title, setTitle] = useState(event?.title ?? "");
  const [description, setDescription] = useState(event?.description ?? "");
  const [eventDate, setEventDate] = useState(event?.eventDate ?? "");
  const [startTime, setStartTime] = useState(event?.startTime?.slice(0, 5) ?? "");
  const [endTime, setEndTime] = useState(event?.endTime?.slice(0, 5) ?? "");
  const [locationAddress, setLocationAddress] = useState(event?.locationAddress ?? "");
  const [vehicleId, setVehicleId] = useState(event?.vehicleId != null ? String(event.vehicleId) : "");

  const validate = () => {
    if (!title.trim()) {
      alert("Validation", "Title is required.", "warning");
      return false;
    }
    if (!eventDate) {
      alert("Validation", "Event date is required.", "warning");
      return false;
    }
    if (!locationAddress.trim()) {
      alert("Validation", "Location address is required.", "warning");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setShowLoader(true);

    const payload = {
      title: title.trim(),
      description: description.trim() || null,
      eventDate,
      startTime: startTime ? `${startTime}:00` : null,
      endTime: endTime ? `${endTime}:00` : null,
      locationAddress: locationAddress.trim(),
      vehicleId: vehicleId ? Number(vehicleId) : null,
    };

    try {
      if (isEdit) {
        await put(API.ADMIN.EVENTS.UPDATE(event.id), payload);
        alert("Updated", `"${title}" updated.`, "success");
      } else {
        await post(API.ADMIN.EVENTS.CREATE, payload);
        alert("Created", `"${title}" added.`, "success");
      }
      await invalidateAdminEvents();
      onClose();
    } catch (err) {
      alert("Error", err?.data?.detail || "Failed to save event.", "error");
    } finally {
      setShowLoader(false);
    }
  };

  const inputClass = "w-full border-2 border-charcoal rounded-md p-2 font-body text-ink";
  const labelClass = "font-heading tracking-wider text-teal text-sm block mb-1";

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-charcoal/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="w-full max-w-lg bg-white rounded-2xl border-2 border-charcoal shadow-[6px_6px_0_rgba(43,39,36,0.18)] p-6 my-8"
                data-testid="event-modal"
              >
                <div className="flex items-center justify-between mb-5">
                  <Dialog.Title className="font-display text-2xl text-charcoal uppercase">
                    {isEdit ? "EDIT EVENT" : "ADD EVENT"}
                  </Dialog.Title>
                  <button onClick={onClose} aria-label="Close modal" className="text-charcoal/40 hover:text-charcoal">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="event-title" className={labelClass}>TITLE *</label>
                    <input
                      id="event-title" type="text" maxLength={200}
                      value={title} onChange={(e) => setTitle(e.target.value)}
                      className={inputClass} data-testid="event-title-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="event-date" className={labelClass}>DATE *</label>
                    <input
                      id="event-date" type="date"
                      value={eventDate} onChange={(e) => setEventDate(e.target.value)}
                      className={inputClass} data-testid="event-date-input"
                    />
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label htmlFor="event-start" className={labelClass}>START TIME</label>
                      <input
                        id="event-start" type="time"
                        value={startTime} onChange={(e) => setStartTime(e.target.value)}
                        className={inputClass} data-testid="event-start-input"
                      />
                    </div>
                    <div className="flex-1">
                      <label htmlFor="event-end" className={labelClass}>END TIME</label>
                      <input
                        id="event-end" type="time"
                        value={endTime} onChange={(e) => setEndTime(e.target.value)}
                        className={inputClass} data-testid="event-end-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="event-location" className={labelClass}>LOCATION ADDRESS *</label>
                    <input
                      id="event-location" type="text" maxLength={300}
                      value={locationAddress} onChange={(e) => setLocationAddress(e.target.value)}
                      className={inputClass} data-testid="event-location-input"
                    />
                    <UseMyLocationButton
                      onResolve={setLocationAddress}
                      testId="event-use-my-location"
                    />
                  </div>

                  <div>
                    <label htmlFor="event-vehicle" className={labelClass}>RIG (OPTIONAL)</label>
                    <select
                      id="event-vehicle"
                      value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}
                      className={`${inputClass} bg-white`} data-testid="event-vehicle-select"
                    >
                      <option value="">— None —</option>
                      {vehicles.map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="event-desc" className={labelClass}>DESCRIPTION</label>
                    <textarea
                      id="event-desc" rows={3} maxLength={1000}
                      value={description} onChange={(e) => setDescription(e.target.value)}
                      className={inputClass} data-testid="event-description-input"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleSubmit}
                    className="flex-1 bg-rust text-white font-heading tracking-widest py-2 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
                    data-testid="event-submit-button"
                  >
                    {isEdit ? "SAVE" : "CREATE"}
                  </button>
                  <button
                    onClick={onClose}
                    className="flex-1 border-2 border-teal text-teal font-heading tracking-wider py-2 rounded-xl hover:bg-teal hover:text-white transition-colors"
                  >
                    CANCEL
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

// ── Delete confirmation ─────────────────────────────────────────────────────

function DeleteEventDialog({ open, onClose, onConfirm, eventTitle }) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-charcoal/50" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
            leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel
              className="w-full max-w-sm bg-white rounded-2xl border-2 border-charcoal shadow-[6px_6px_0_rgba(43,39,36,0.18)] p-6"
              data-testid="delete-event-dialog"
            >
              <Dialog.Title className="font-display text-2xl text-charcoal uppercase mb-3">
                DELETE EVENT
              </Dialog.Title>
              <p className="font-body text-charcoal/70 mb-6">
                Delete <strong>"{eventTitle}"</strong>? This is permanent and cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={onConfirm}
                  className="flex-1 bg-rust text-white font-heading tracking-widest py-2 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
                  data-testid="confirm-delete-event-button"
                >
                  DELETE
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 border-2 border-teal text-teal font-heading tracking-wider py-2 rounded-xl hover:bg-teal hover:text-white transition-colors"
                >
                  CANCEL
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

// ── Event admin page ────────────────────────────────────────────────────────

/**
 * EventAdmin — Admin event management with full CRUD (F-018, T-005).
 *
 * Lists events in a table, opens a shared create/edit modal, and supports
 * hard-delete with confirmation (CON-002 §9). Authed reads via SWR; writes
 * via useApi (DEF-010).
 */
export default function EventAdmin() {
  const alert = useAlert();
  const { put, del } = useApi();
  const { setShowLoader } = useLoaderStore();
  const { events, isLoading, error } = useAdminEvents();
  const { vehicles } = useAdminVehicles();

  const [modalOpen, setModalOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Remount key — blank-on-create / prefilled-on-edit / clear-on-success (FE-3).
  const [modalSession, setModalSession] = useState(0);

  const openCreate = () => { setEditEvent(null); setModalSession((n) => n + 1); setModalOpen(true); };
  const openEdit = (ev) => { setEditEvent(ev); setModalSession((n) => n + 1); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditEvent(null); };

  const handleToggleActive = async (ev) => {
    // Events gain isActive via DeactivatedAt (CON-002 §9A). Default to Active
    // when the DTO omits the flag.
    const current = ev.isActive != null ? ev.isActive : true;
    setShowLoader(true);
    try {
      await put(API.ADMIN.EVENTS.UPDATE(ev.id), { isActive: !current });
      await invalidateAdminEvents();
      alert("Updated", `"${ev.title}" ${!current ? "activated" : "deactivated"}.`, "success");
    } catch (err) {
      alert("Error", err?.data?.detail || "Failed to update event.", "error");
    } finally {
      setShowLoader(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setShowLoader(true);
    try {
      await del(API.ADMIN.EVENTS.DELETE(deleteTarget.id));
      await invalidateAdminEvents();
      alert("Deleted", `"${deleteTarget.title}" deleted.`, "success");
      setDeleteTarget(null);
    } catch (err) {
      alert("Error", err?.data?.detail || "Failed to delete event.", "error");
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <main data-testid="admin-events-page" className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl text-charcoal uppercase">Event Management</h1>
          <p className="font-body text-charcoal/60 text-sm mt-1">
            Schedule the markets, festivals, and pop-ups the Bullet rolls to.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="bg-rust text-white font-heading tracking-widest px-5 py-2 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
          data-testid="add-event-button"
        >
          + ADD EVENT
        </button>
      </div>

      {isLoading && <p className="font-body text-charcoal/60">Loading events…</p>}
      {error && <p className="font-body text-rust">Unable to load events.</p>}

      {!isLoading && !error && (
        <div className="overflow-x-auto">
          <table className="w-full border-2 border-charcoal rounded-2xl overflow-hidden">
            <thead className="bg-charcoal text-white">
              <tr>
                {["Date", "Title", "Location", "Rig", "Active", "Actions"].map((h) => (
                  <th key={h} className="font-heading tracking-wider text-left px-4 py-3 text-sm">
                    {h.toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-10 font-body text-charcoal/50">
                    No events yet. Add one above.
                  </td>
                </tr>
              )}
              {events.map((ev) => (
                <tr
                  key={ev.id}
                  className="border-b border-aluminum/40 hover:bg-cream/50 transition-colors"
                  data-testid={`event-row-${ev.id}`}
                >
                  <td className="px-4 py-3 font-body text-charcoal/70 text-sm whitespace-nowrap">
                    {formatEventDate(ev.eventDate)}
                  </td>
                  <td className="px-4 py-3 font-body text-charcoal">{ev.title}</td>
                  <td className="px-4 py-3 font-body text-charcoal/70 text-sm">{ev.locationAddress}</td>
                  <td className="px-4 py-3 font-body text-charcoal/60 text-sm">
                    {ev.vehicleName || <span className="italic text-charcoal/40">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <ActiveToggle
                      enabled={ev.isActive != null ? ev.isActive : true}
                      onToggle={() => handleToggleActive(ev)}
                      label={`Toggle ${ev.title} active`}
                      testId={`toggle-active-${ev.id}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEdit(ev)}
                        className="font-heading tracking-wider text-xs text-teal border-2 border-teal px-3 py-1 rounded-full hover:bg-teal hover:text-white transition-colors"
                        data-testid={`edit-event-${ev.id}`}
                      >
                        EDIT
                      </button>
                      <button
                        onClick={() => setDeleteTarget(ev)}
                        className="font-heading tracking-wider text-xs text-rust border-2 border-rust px-3 py-1 rounded-full hover:bg-rust hover:text-white transition-colors"
                        data-testid={`delete-event-${ev.id}`}
                      >
                        DELETE
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <EventModal
        key={`${editEvent ? `edit-${editEvent.id}` : "create"}-${modalSession}`}
        open={modalOpen}
        onClose={closeModal}
        event={editEvent}
        vehicles={vehicles}
      />

      <DeleteEventDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        eventTitle={deleteTarget?.title ?? ""}
      />
    </main>
  );
}
